import AsyncStorage from '@react-native-async-storage/async-storage';
import { reloadAppAsync } from 'expo';
import { I18nManager, Platform, type TextStyle, type ViewStyle } from 'react-native';
import { currentLanguage, type UiLanguage } from './index';

/**
 * SENS D'ÉCRITURE DE L'INTERFACE — de droite à gauche pour l'arabe, de gauche à droite sinon.
 *
 * Piloté ici, en JavaScript, et NON par l'option `supportsRTL` du plugin expo-localization : celle-ci
 * recalcule le sens à CHAQUE lancement d'après la langue de l'APPAREIL, avant React
 * (`LocalizationModule.swift` et `.kt`, vérifié le 17/10/2026). Elle écraserait le choix fait dans
 * l'app : une interface arabe sur un téléphone français resterait de gauche à droite, une interface
 * française sur un téléphone en arabe passerait de droite à gauche. Aucune modification d'app.json
 * n'est donc nécessaire ; le manifeste Android porte déjà `android:supportsRtl="true"`.
 *
 * React Native ne lit le sens qu'au démarrage : en changer impose de relancer l'application. UNE
 * tentative par sens visé : si le système ne l'applique pas, on ne relance pas en boucle.
 *
 * Le web n'a pas d'I18nManager : il suit l'attribut `dir` de la page, posé ici.
 */
const CLE_RELANCE = 'ui_sens_relance';

export function estDeDroiteAGauche(language: UiLanguage): boolean {
  return language === 'ar';
}

/** Langue de la page web : sert au lecteur d'écran, à la césure, et au choix des glyphes chinois. */
const LANG_HTML: Record<UiLanguage, string> = { fr: 'fr', en: 'en', es: 'es', pt: 'pt-BR', ar: 'ar', zh: 'zh-Hans' };

/** Sens RÉEL de l'interface affichée : celui du lancement en natif, celui de la langue sur le web. */
export function interfaceRtl(): boolean {
  return Platform.OS === 'web' ? estDeDroiteAGauche(currentLanguage()) : I18nManager.isRTL;
}

/** Passer à `language` change-t-il le sens, donc impose-t-il une relance de l'application ? */
export function relanceNecessaire(language: UiLanguage): boolean {
  return Platform.OS !== 'web' && estDeDroiteAGauche(language) !== I18nManager.isRTL;
}

/**
 * Miroir des icônes de DIRECTION (retour, flèche, chevron) en droite à gauche. Jamais pour celles
 * qui ne désignent pas un sens : lecture, logos, graphiques, horloges, chiffres.
 */
export function styleMiroir(): ViewStyle | undefined {
  return interfaceRtl() ? { transform: [{ scaleX: -1 }] } : undefined;
}

const FLECHES_INVERSEES: Record<string, string> = { '→': '←', '←': '→', '›': '‹', '‹': '›' };

/** Flèche écrite en texte (« → », « › »), retournée en droite à gauche. */
export function fleche(caractere: string): string {
  return interfaceRtl() ? FLECHES_INVERSEES[caractere] ?? caractere : caractere;
}

/**
 * Contenu qui reste de gauche à droite dans une interface de droite à gauche : CV et lettres
 * générés, leurs modèles et aperçus, texte des offres. Ils sont en français et partent chez des
 * recruteurs français.
 */
export const BLOC_LTR: ViewStyle = { direction: 'ltr' };
export const TEXTE_LTR: TextStyle = { writingDirection: 'ltr', textAlign: 'left' };
/** Sur le web, react-native-web lit le sens dans l'attribut `dir`, pas dans le style `direction`. */
export const PROPS_LTR = { dir: 'ltr' } as object;

/**
 * Valeur française insérée dans une phrase arabe (intitulé cité, durée, salaire) : isolée de gauche
 * à droite (LRI … PDI), sans quoi l'algorithme bidirectionnel la coupe et déplace ses guillemets.
 */
export function isolerLtr(texte: string): string {
  return interfaceRtl() ? `\u2066${texte}\u2069` : texte;
}

/**
 * Valeur française CITÉE dans une phrase arabe (intitulé de poste, titre d'un CV ou d'une lettre) :
 * isolée AVEC ses guillemets, qui restent « à la française » autour d'elle. Laissés dans le gabarit
 * arabe, hors de l'isolation, ils se retournaient et se coupaient (relecture du 17/10/2026). Les
 * gabarits arabes n'en portent donc pas ; les autres langues gardent les leurs et reçoivent la valeur
 * nue.
 */
export function citation(texte: string): string {
  return interfaceRtl() ? `\u2066«${texte}»\u2069` : texte;
}

/**
 * WEB, ARABE : une seule pile de polices, la police système arabe en tête. Sans elle, le navigateur
 * choisissait la police de repli glyphe par glyphe et selon la graisse : puces non sélectionnées,
 * rythmes et sous-titres tombaient sur une autre police arabe que les titres (relecture du
 * 17/10/2026). Rien n'est embarqué ; le natif garde la police système, qui n'a pas ce défaut.
 * Le sélecteur `html[lang="ar"] …` l'emporte sur les classes de react-native-web.
 *
 * Même feuille : les textes de react-native-web portent dir="auto", donc une direction lue sur le
 * PREMIER caractère. Une phrase arabe commençant par « LinkedIn » devenait un paragraphe de gauche à
 * droite, son point final du mauvais côté. En arabe, la direction est forcée de droite à gauche ;
 * les blocs gardés de gauche à droite (dir="ltr" : CV, lettres, offres) gardent la leur.
 */
const POLICE_ARABE_WEB =
  '"SF Arabic", "Geeza Pro", "Segoe UI", "Noto Sans Arabic", "Noto Naskh Arabic", Tahoma, -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif';
const ID_STYLE_ARABE = 'hormeia-police-arabe';

function installerPoliceArabeWeb(): void {
  if (document.getElementById(ID_STYLE_ARABE)) return;
  const style = document.createElement('style');
  style.id = ID_STYLE_ARABE;
  style.textContent = [
    `html[lang="ar"] div, html[lang="ar"] span, html[lang="ar"] input, html[lang="ar"] textarea { font-family: ${POLICE_ARABE_WEB}; }`,
    'html[lang="ar"] div[dir="auto"], html[lang="ar"] span[dir="auto"] { direction: rtl; }',
    'html[lang="ar"] [dir="ltr"] div[dir="auto"], html[lang="ar"] [dir="ltr"] span[dir="auto"] { direction: ltr; }',
  ].join('\n');
  document.head.appendChild(style);
}

export async function appliquerSens(language: UiLanguage): Promise<void> {
  if (Platform.OS === 'web') {
    // `document` n'existe que dans le navigateur : l'export statique n'exécute pas ce code.
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', estDeDroiteAGauche(language) ? 'rtl' : 'ltr');
      document.documentElement.setAttribute('lang', LANG_HTML[language]);
      installerPoliceArabeWeb();
    }
    return;
  }
  const rtl = estDeDroiteAGauche(language);
  I18nManager.allowRTL(rtl);
  I18nManager.forceRTL(rtl);
  const vise = rtl ? 'rtl' : 'ltr';
  try {
    if (I18nManager.isRTL === rtl) {
      if ((await AsyncStorage.getItem(CLE_RELANCE)) !== null) await AsyncStorage.removeItem(CLE_RELANCE);
      return;
    }
    if ((await AsyncStorage.getItem(CLE_RELANCE)) === vise) return;
    await AsyncStorage.setItem(CLE_RELANCE, vise);
    await reloadAppAsync();
  } catch {
    // Le sens demandé s'appliquera au prochain lancement : jamais bloquant.
  }
}
